import { debounce } from 'lodash-es';
import { useEffect, useMemo, useRef } from 'react';

type DebounceSettings = Parameters<typeof debounce>[2];

/**
 * A debounced callback for React
 */
// oxlint-disable typescript/no-explicit-any -- pre-existing explicit any; replace with precise types when touched
export function useDebounceCallback<C extends (...args: any[]) => any>(
  callback: C,
  delay: number,
  options?: DebounceSettings,
) {
  // oxlint-enable typescript/no-explicit-any
  const callbackRef = useRef(callback);
  callbackRef.current = callback;

  const debouncedCallback = useMemo(() => {
    return debounce((...args: Parameters<C>) => callbackRef.current(...args), delay, options);
  }, [delay, options]);

  // Cleanup to cancel debounce on unmount
  useEffect(() => {
    return () => debouncedCallback.cancel();
  }, [debouncedCallback]);

  // A `DebouncedFunc` takes the wrapped callback's arguments, but returns `undefined`
  // when a call is deferred (and also exposes `cancel`/`flush`).
  return debouncedCallback;
}
